import type { TFunction } from 'i18next';
import { z } from 'zod/v4';

import { isValidEmail, isValidName, isValidPhoneNumber } from '@/utils';
import type { PaymentMethod } from '@/utils/paymentMethods';

/**
 * The namespace these schemas' messages are resolved against.
 *
 * Note that the gift-card flow also renders these strings: `useConfirmOrderForm`
 * passes this same namespace for both checkout and gift cards, which is why the
 * `ConfirmGiftCardBottomSheet.userInformation.validation` block in the locale
 * files is currently unreachable. Left as-is deliberately — see SQR-14928.
 */
export type UserInformationT = TFunction<
  'common',
  'ConfirmBookingBottomSheet.userInformation.validation'
>;

/**
 * Built as a factory rather than a module-scope const so the messages re-derive
 * when the user switches language. The previous module-scope
 * `i18n.getFixedT(i18n.language, …)` froze every message to whatever language
 * was active at import time — see SQR-14928.
 */
export const createUserInformationSchema = (t: UserInformationT) =>
  z.object({
    firstName: z
      .string()
      .trim()
      .min(1, t('firstNameRequired'))
      .refine(isValidName, t('firstNameInvalid')),
    lastName: z
      .string()
      .trim()
      .min(1, t('lastNameRequired'))
      .refine(isValidName, t('lastNameInvalid')),
    phone: z.string().refine(isValidPhoneNumber, {
      message: t('phoneInvalid'),
    }),
    email: z
      .string()
      .trim()
      .min(1, t('emailRequired'))
      .refine(isValidEmail, t('emailInvalid')),
  });

export const createConfirmOrderFormSchema = (t: UserInformationT) =>
  createUserInformationSchema(t).extend({
    paymentMethod: z
      .custom<PaymentMethod>()
      .refine((val) => val !== undefined, {
        message: t('paymentMethodRequired'),
      }),
  });

export type ConfirmOrderFormValues = z.infer<
  ReturnType<typeof createConfirmOrderFormSchema>
>;
